import React, { FC, createContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { LoginDataType, PropertyType, DASHBOARD_URL } from '../../common';
import { api, bov3_api } from '../../api';

let logOutTimer: ReturnType<typeof setTimeout>;

interface AuthContextType {
	user?: PropertyType | null;
	uid: string;
	token: string;
	loginType: string;
	isLoggedIn: boolean;
	setUser: (data: PropertyType) => void;
	setLoginType: (data: string) => void;
	login: (data: LoginDataType) => void;
	logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
	uid: '',
	token: '',
	isLoggedIn: false,
	loginType: '',
	setUser: () => {},
	setLoginType: () => {},
	login: () => {},
	logout: () => {},
});

interface AuthProps {
	children: JSX.Element;
}

const storeLogin = (data: LoginDataType) => {
	localStorage.setItem('user_data', JSON.stringify(data));
};

const clearStorage = () => {
	localStorage.removeItem('user_data');
};

const remainingTime = (exp: string) => {
	const current = new Date().getTime();
	const expTime = new Date(exp).getTime();
	return expTime - current;
};

const getInitials = () => {
	const data = localStorage.getItem('user_data');
	const inData = data && JSON.parse(data);

	if (!inData || remainingTime(inData.tokenExpiration) <= 0) {
		clearStorage();
		return;
	}
	return inData;
};

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour

const AuthContextProvider: FC<AuthProps> = ({ children }): JSX.Element => {
	const inData = getInitials();
	const [user, setUser] = useState<PropertyType | null>(null);
	const [uid, setUid] = useState(inData ? inData.uid : null);
	const [token, setToken] = useState(inData ? inData.token : '');
	const [loginType, setLoginType] = useState('');
	const history = useHistory();

	const onLogOut = () => {
		setToken('');
		setUser(null);
		clearStorage();
		clearTimeout(logOutTimer);
		history.replace('/login');
	};

	const resetLogOutTimer = () => {
		clearTimeout(logOutTimer);
		if (token) {
			logOutTimer = setTimeout(onLogOut, INACTIVITY_TIMEOUT);
		}
	};

	const onLogIn = (data: LoginDataType) => {
		setUid(data.uid);
		setToken(data.token);
		storeLogin(data);

		resetLogOutTimer();
		history.replace(DASHBOARD_URL.value);
	};

	useEffect(() => {
		if (token) {
			resetLogOutTimer();

			const events = ['mousedown', 'keypress', 'scroll', 'touchstart'];
			const handleActivity = () => resetLogOutTimer();

			events.forEach((event) => document.addEventListener(event, handleActivity));

			const interceptor = api.interceptors.request.use((config) => {
				resetLogOutTimer();
				return config;
			});

			const bov3Interceptor = bov3_api.interceptors.request.use((config) => {
				resetLogOutTimer();
				return config;
			});

			return () => {
				events.forEach((event) => document.removeEventListener(event, handleActivity));
				api.interceptors.request.eject(interceptor);
				bov3_api.interceptors.request.eject(bov3Interceptor);
				clearTimeout(logOutTimer);
			};
		}
	}, [token]);

	api.interceptors.response.use(
		(request) => request,
		(error) => {
			const url = error?.response?.config?.url;
			if (error?.response?.status === 401 && !(url.includes('login') || url.includes('reset'))) {
				onLogOut();
			}
			return Promise.reject({ ...error });
		}
	);

	const value = {
		user: user,
		uid: uid,
		token: token,
		loginType: loginType,
		isLoggedIn: !!token,
		setUser: setUser,
		login: onLogIn,
		logout: onLogOut,
		setLoginType: setLoginType,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthContextProvider };
